import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { LOCALIZED_SHARED_ROUTES, LocalizedRouteService, ProfileState, SHARED_ROUTE_IDS } from '@lexplus-shared-lib';
import { LexplusPermissionsEnum } from '@lexplus-authentication-api';

@Service()
export class RequiresPermissionService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly localizedRouteService = inject(LocalizedRouteService);

  public canActivate(next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    if (this.hasRequiredPermission(next)) {
      return true;
    }

    const errorRoute = this.localizedRouteService.getLocalizedRoute(LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ERROR]);
    void this.router.navigateByUrl(`/${errorRoute}?errorNumber=403`);
    return false;
  }

  /**
   * A route names either a single `requiredPermission` or, under
   * `requiredAnyPermission`, a list of which one suffices. With neither, the route
   * is unprotected.
   */
  private hasRequiredPermission(next: ActivatedRouteSnapshot): boolean {
    const anyPermission = next.data['requiredAnyPermission'] as LexplusPermissionsEnum[] | undefined;
    if (anyPermission?.length) {
      return anyPermission.some((permission) => this.store.selectSnapshot(ProfileState.hasPermission(permission)));
    }

    const requiredPermission = next.data['requiredPermission'] as LexplusPermissionsEnum | undefined;
    return !requiredPermission || this.store.selectSnapshot(ProfileState.hasPermission(requiredPermission));
  }
}

export const RequiresPermissionGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
  return <boolean>inject(RequiresPermissionService).canActivate(next, state);
};
